import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  ARTEN, artLabel, REASON_OPTIONS, STATUS_OPTIONS, formatDateDE, countAttendance,
} from "@/lib/training";
import { positionAbbrev, positionColor } from "@/lib/playerConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, MapPin, Clock, Calendar, Pencil, Trash2, Save, X,
  CheckCircle2, AlertCircle, XCircle, ClipboardList, ChevronRight, Copy, Share2,
} from "lucide-react";
import { toast } from "sonner";
import DuplicateSessionDialog from "@/components/DuplicateSessionDialog";
import { buildSessionShareUrl } from "@/lib/shareUtils";

export default function TrainingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendance, setAttendance] = useState({}); // {playerId: {status, reason}}
  const [reasonDialogPlayer, setReasonDialogPlayer] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [metaForm, setMetaForm] = useState({});
  const [hasSession, setHasSession] = useState(false);
  const [session, setSession] = useState(null);
  const [templateSession, setTemplateSession] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [sessionsList, setSessionsList] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const [t, p, s, allT, allS] = await Promise.all([
        api.get(`/trainings/${id}`),
        api.get("/players"),
        api.get(`/trainings/${id}/session`).catch(() => ({ data: null })),
        api.get("/trainings"),
        api.get("/training-sessions"),
      ]);
      setTraining(t.data);
      setAttendance(t.data.attendance || {});
      setPlayers(p.data);
      setHasSession(!!s.data);
      setSession(s.data);
      setTrainings(allT.data);
      setSessionsList(allS.data);
      setMetaForm({
        datum: t.data.datum,
        uhrzeit: t.data.uhrzeit,
        end_zeit: t.data.end_zeit || "",
        ort: t.data.ort,
        art: t.data.art,
      });
    } catch {
      toast.error("Training nicht gefunden");
      navigate("/training");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (playerId, status, reason) => {
    const next = { ...attendance };
    if (!status) {
      delete next[playerId];
    } else {
      next[playerId] = reason ? { status, reason } : { status };
    }
    setAttendance(next);
    try {
      await api.put(`/trainings/${id}/attendance`, { attendance: next });
    } catch (e) {
      toast.error("Konnte Anwesenheit nicht speichern");
    }
  };

  const handleStatusClick = (playerId, status) => {
    if (attendance[playerId]?.status === status) {
      setStatus(playerId, null);
      return;
    }
    if (status === "entschuldigt") {
      // Save status immediately, open reason picker
      setStatus(playerId, "entschuldigt", attendance[playerId]?.reason);
      setReasonDialogPlayer(playerId);
    } else {
      setStatus(playerId, status);
    }
  };

  const setReason = (playerId, reason) => {
    setStatus(playerId, "entschuldigt", reason);
    setReasonDialogPlayer(null);
  };

  const counts = useMemo(() => {
    const c = { anwesend: 0, entschuldigt: 0, unentschuldigt: 0, total_recorded: 0 };
    for (const v of Object.values(attendance)) {
      if (c[v.status] !== undefined) { c[v.status]++; c.total_recorded++; }
    }
    return c;
  }, [attendance]);

  const shareSession = async () => {
    if (!session?.share_token) {
      toast.error("Kein Freigabelink verfügbar");
      return;
    }
    const url = buildSessionShareUrl(training?.datum, session?.name, session.share_token);
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Trainingslink kopiert");
    } catch {
      toast.error("Kopieren nicht möglich");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/trainings/${id}`);
      toast.success("Training gelöscht");
      navigate("/training");
    } catch {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  const saveMeta = async () => {
    try {
      const payload = { ...metaForm };
      if (payload.end_zeit === "") payload.end_zeit = null;
      const { data } = await api.put(`/trainings/${id}`, payload);
      setTraining(data);
      setEditingMeta(false);
      toast.success("Training aktualisiert");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    }
  };

  if (loading) return <div className="text-center py-20 text-zinc-500 tracking-wide">Lädt…</div>;
  if (!training) return null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <button
        onClick={() => navigate("/training")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="td-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück
      </button>

      {/* Header */}
      <div className={"rounded-lg border bg-zinc-900/40 p-5 mb-5 " + (hasSession ? "border-zinc-800 border-l-4 border-l-emerald-500" : "border-zinc-800")}>
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="flex-1">
            <div className={"inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium tracking-wide mb-2 " + (training.art === "bonustraining" ? "text-amber-300 bg-amber-500/10" : "text-brand-lighter bg-brand/10")}>
              {artLabel(training.art)}
            </div>
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-zinc-100 tracking-tight" data-testid="td-title">
              {formatDateDE(training.datum)}
            </h1>
            <div className="flex flex-wrap items-center gap-3 mt-1.5 text-sm text-zinc-400">
              <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {training.uhrzeit}{training.end_zeit ? ` – ${training.end_zeit}` : ""}</span>
              <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {training.ort}</span>
              {training.series_id && <span className="text-[10px] uppercase tracking-wide text-zinc-500">Aus Serie</span>}
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setEditingMeta(true)}
              className="bg-transparent border-zinc-700 text-zinc-100 hover:text-brand-lighter hover:bg-zinc-900 rounded-md h-10"
              data-testid="td-edit-btn"
            >
              <Pencil className="h-4 w-4 mr-2" /> Bearbeiten
            </Button>
            <Button
              variant="outline"
              onClick={() => setConfirmDelete(true)}
              className="bg-transparent border-zinc-700 text-zinc-100 hover:text-red-400 hover:bg-zinc-900 rounded-md h-10"
              data-testid="td-delete-btn"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* counts */}
        <div className="grid grid-cols-3 gap-2 mt-4 pt-4 border-t border-zinc-800">
          <CountBox label="Anwesend" value={counts.anwesend} color="text-emerald-400" icon={CheckCircle2} testId="count-anwesend" />
          <CountBox label="Entschuldigt" value={counts.entschuldigt} color="text-amber-400" icon={AlertCircle} testId="count-entschuldigt" />
          <CountBox label="Unentschuldigt" value={counts.unentschuldigt} color="text-rose-400" icon={XCircle} testId="count-unentschuldigt" />
        </div>
      </div>

      {/* Trainingseinheit-Karte */}
      <div
        className={
          "rounded-lg border bg-zinc-900/40 mb-5 " +
          (hasSession ? "border-emerald-500/40" : "border-zinc-800")
        }
      >
        <button
          type="button"
          onClick={() => navigate(hasSession
            ? `/training/${id}/einheit/ansehen`
            : `/training/${id}/einheit/bearbeiten`)}
          className={
            "w-full text-left hover:bg-zinc-900/60 transition p-4 flex items-center gap-3 " +
            (hasSession ? "rounded-t-lg" : "rounded-lg")
          }
          data-testid="td-session-card"
        >
          <div className={
            "h-10 w-10 rounded-md flex items-center justify-center shrink-0 " +
            (hasSession ? "bg-emerald-500/15 text-emerald-400" : "bg-brand/15 text-brand-lighter")
          }>
            <ClipboardList className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500">Trainingseinheit</div>
            <div className={"text-sm font-medium " + (hasSession ? "text-emerald-300" : "text-zinc-100")}>
              {hasSession
                ? (session?.name?.trim() || "Trainingsplan ansehen")
                : "Trainingsplan anlegen"}
            </div>
          </div>
          <ChevronRight className={"h-5 w-5 " + (hasSession ? "text-emerald-500/60" : "text-zinc-600")} />
        </button>

        {hasSession && session && (
          <div className="border-t border-emerald-500/20 grid grid-cols-2 divide-x divide-emerald-500/20">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setTemplateSession(session); }}
              className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs font-medium tracking-wide text-emerald-300 hover:bg-emerald-500/10 transition px-2"
              data-testid="td-use-as-template-btn"
            >
              <Copy className="h-4 w-4" /> Als Vorlage nutzen
            </button>
            <button
              type="button"
              onClick={shareSession}
              className="min-h-[44px] inline-flex items-center justify-center gap-2 text-xs font-medium tracking-wide text-emerald-300 hover:bg-emerald-500/10 transition px-2"
              data-testid="td-share-session-btn"
              aria-label="Training teilen"
            >
              <Share2 className="h-4 w-4" /> Training teilen
            </button>
          </div>
        )}
      </div>

      {/* Player list */}
      <div className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Anwesenheitsliste</div>
      {players.length === 0 ? (
        <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-8 text-center text-zinc-500 text-sm">
          Keine Spieler im Kader. Lege zuerst Spieler an.
        </div>
      ) : (
        <div className="space-y-2" data-testid="attendance-list">
          {players.map((p) => {
            const entry = attendance[p.id];
            const reasonLabel = entry?.reason ? REASON_OPTIONS.find((r) => r.key === entry.reason)?.label : null;
            return (
              <div
                key={p.id}
                className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 sm:p-4"
                data-testid={`attendance-row-${p.id}`}
              >
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="text-zinc-100 font-medium">{p.name}</div>
                    <div className={"text-[11px] font-medium tracking-wide " + positionColor(p.position)}>
                      {positionAbbrev(p.position)} · {p.jahrgang}
                      {reasonLabel && <span className="text-amber-400 ml-2">· {reasonLabel}</span>}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5 sm:flex sm:gap-2">
                    {STATUS_OPTIONS.map((s) => {
                      const active = entry?.status === s.key;
                      return (
                        <button
                          key={s.key}
                          onClick={() => handleStatusClick(p.id, s.key)}
                          className={
                            "min-h-[40px] px-3 rounded-md text-xs font-semibold tracking-wide transition border " +
                            (active
                              ? s.border + " ring-1 ring-current"
                              : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-100 hover:border-zinc-700")
                          }
                          data-testid={`status-${p.id}-${s.key}`}
                        >
                          {s.label.slice(0, active ? s.label.length : 12)}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Reason dialog */}
      <Dialog open={!!reasonDialogPlayer} onOpenChange={(o) => !o && setReasonDialogPlayer(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold tracking-tight" data-testid="reason-dialog-title">
              Grund für Entschuldigung
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2 pt-2">
            <div className="grid grid-cols-2 gap-2">
              {REASON_OPTIONS.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setReason(reasonDialogPlayer, r.key)}
                  className="min-h-[44px] rounded-md border border-zinc-800 bg-zinc-950 hover:border-brand-lighter/40 hover:bg-zinc-800 text-zinc-100 text-sm font-medium transition"
                  data-testid={`reason-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
            <Button
              variant="ghost"
              onClick={() => setReasonDialogPlayer(null)}
              className="w-full text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 mt-2"
              data-testid="reason-skip-btn"
            >
              Ohne Grund speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit meta dialog */}
      <Dialog open={editingMeta} onOpenChange={setEditingMeta}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="font-display text-lg font-semibold tracking-tight">Training bearbeiten</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label className="text-xs text-zinc-400">Datum</Label>
              <Input
                type="date"
                value={metaForm.datum || ""}
                onChange={(e) => setMetaForm({ ...metaForm, datum: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 mt-1 rounded-md"
                data-testid="meta-datum"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Uhrzeit (Start – Ende)</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="time"
                  value={metaForm.uhrzeit || ""}
                  onChange={(e) => setMetaForm({ ...metaForm, uhrzeit: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 rounded-md max-w-[140px]"
                  data-testid="meta-uhrzeit"
                />
                <span className="text-zinc-500">–</span>
                <Input
                  type="time"
                  value={metaForm.end_zeit || ""}
                  onChange={(e) => setMetaForm({ ...metaForm, end_zeit: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 rounded-md max-w-[140px]"
                  data-testid="meta-end-zeit"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Ort</Label>
              <Input
                value={metaForm.ort || ""}
                onChange={(e) => setMetaForm({ ...metaForm, ort: e.target.value })}
                className="bg-zinc-950 border-zinc-800 text-zinc-100 h-10 mt-1 rounded-md"
                data-testid="meta-ort"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Trainingsart</Label>
              <div className="flex gap-2 mt-1.5">
                {ARTEN.map((a) => (
                  <button
                    key={a.key}
                    onClick={() => setMetaForm({ ...metaForm, art: a.key })}
                    className={
                      "min-h-[40px] px-3 rounded-md border text-sm transition flex-1 " +
                      (metaForm.art === a.key
                        ? "bg-brand border-brand text-white"
                        : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-700")
                    }
                    data-testid={`meta-art-${a.key}`}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setEditingMeta(false)} className="flex-1 bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-800">
                <X className="h-4 w-4 mr-1" /> Abbrechen
              </Button>
              <Button onClick={saveMeta} className="flex-1 bg-brand hover:bg-brand-light text-white font-semibold" data-testid="meta-save-btn">
                <Save className="h-4 w-4 mr-1" /> Speichern
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Training löschen?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Dieses Training wird unwiderruflich entfernt. Bei Serienterminen werden andere Einheiten nicht beeinflusst.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 border-zinc-700 text-zinc-100 hover:bg-zinc-700">Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600 text-white" data-testid="td-delete-confirm">Löschen</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <DuplicateSessionDialog
        source={templateSession}
        trainings={trainings}
        sessions={sessionsList}
        todayIso={new Date().toISOString().slice(0, 10)}
        onClose={() => setTemplateSession(null)}
        onSuccess={() => { setTemplateSession(null); load(); }}
      />
    </div>
  );
}

function CountBox({ label, value, color, icon: Icon, testId }) {
  return (
    <div className="rounded-md border border-zinc-800 bg-zinc-950 p-3 flex items-center gap-2.5">
      <Icon className={"h-5 w-5 " + color} />
      <div>
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className={"font-display text-xl font-bold " + color} data-testid={testId}>{value}</div>
      </div>
    </div>
  );
}
